import type { AufmassZeile, PositionsAufmass } from './schema';

/**
 * Reine Rechenhelfer für das Aufmaß (Mengenfortschreibung je LV-Position).
 *
 * Modell: Pro Position werden Einzelaufmaße erfasst (Menge + Datum + Notiz).
 * - Ist-Menge  = Summe aller Aufmaß-Mengen
 * - Rest-Menge = Soll-Menge (LV) − Ist-Menge
 * - Auslastung = Ist / Soll × 100 %
 */

export type Warnstufe = 'ok' | 'warnung' | 'voll' | 'ueber';

// Ab dieser Auslastung wird gewarnt (gelb), ab 100 % "voll"/"über".
export const AUFMASS_WARN_SCHWELLE = 90;

function toNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function roundMenge(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

export interface AufmassSummen {
  istMenge: number;
  restMenge: number;
  auslastungProzent: number; // 0..∞ (kann >100 sein)
  warnstufe: Warnstufe;
  anzahlEintraege: number;
  offeneMenge: number; // Ist-Menge, die noch nicht abgerechnet ist
}

/** Summe aller Aufmaß-Mengen (Ist-Menge). */
export function istMenge(aufmass: PositionsAufmass | null | undefined): number {
  if (!aufmass?.zeilen?.length) return 0;
  return roundMenge(aufmass.zeilen.reduce((sum, z) => sum + toNumber(z.menge), 0));
}

/** Noch nicht abgerechnete Ist-Menge. */
export function offeneMenge(aufmass: PositionsAufmass | null | undefined): number {
  if (!aufmass?.zeilen?.length) return 0;
  return roundMenge(
    aufmass.zeilen.filter((z) => !z.abgerechnet).reduce((sum, z) => sum + toNumber(z.menge), 0),
  );
}

export function warnstufeFuer(auslastungProzent: number): Warnstufe {
  if (auslastungProzent > 100.0001) return 'ueber';
  if (auslastungProzent >= 100) return 'voll';
  if (auslastungProzent >= AUFMASS_WARN_SCHWELLE) return 'warnung';
  return 'ok';
}

/** Alle Aufmaß-Kennzahlen einer Position bezogen auf die Soll-Menge. */
export function berechneAufmass(
  aufmass: PositionsAufmass | null | undefined,
  sollMenge: number,
): AufmassSummen {
  const ist = istMenge(aufmass);
  const soll = toNumber(sollMenge);
  const rest = roundMenge(soll - ist);
  const auslastung = soll > 0 ? roundMenge((ist / soll) * 100) : ist > 0 ? Infinity : 0;
  return {
    istMenge: ist,
    restMenge: rest,
    auslastungProzent: auslastung === Infinity ? 100 : auslastung,
    warnstufe: soll > 0 ? warnstufeFuer(auslastung) : ist > 0 ? 'ueber' : 'ok',
    anzahlEintraege: aufmass?.zeilen?.length ?? 0,
    offeneMenge: offeneMenge(aufmass),
  };
}

export function hatAufmass(aufmass: PositionsAufmass | null | undefined): boolean {
  return Boolean(aufmass?.zeilen?.some((z) => toNumber(z.menge) !== 0));
}

let zeilenIdCounter = 0;

export function neueAufmassId(): string {
  zeilenIdCounter += 1;
  return `am-${Date.now().toString(36)}-${zeilenIdCounter}`;
}

/** Fügt ein Einzelaufmaß hinzu und gibt das neue Aufmaß-Objekt zurück. */
export function addAufmassZeile(
  aufmass: PositionsAufmass | null | undefined,
  zeile: Omit<AufmassZeile, 'id'> & { id?: string },
): PositionsAufmass {
  const basis: PositionsAufmass = aufmass ? { zeilen: [...aufmass.zeilen] } : { zeilen: [] };
  basis.zeilen.push({ ...zeile, id: zeile.id ?? neueAufmassId(), menge: toNumber(zeile.menge) });
  return basis;
}

export function updateAufmassZeile(
  aufmass: PositionsAufmass | null | undefined,
  id: string,
  updates: Partial<AufmassZeile>,
): PositionsAufmass {
  const basis: PositionsAufmass = aufmass ? { zeilen: [...aufmass.zeilen] } : { zeilen: [] };
  return {
    zeilen: basis.zeilen.map((z) => (z.id === id ? { ...z, ...updates } : z)),
  };
}

export function removeAufmassZeile(
  aufmass: PositionsAufmass | null | undefined,
  id: string,
): PositionsAufmass {
  const basis: PositionsAufmass = aufmass ? { zeilen: [...aufmass.zeilen] } : { zeilen: [] };
  return { zeilen: basis.zeilen.filter((z) => z.id !== id) };
}

/** Markiert alle offenen Aufmaß-Zeilen als abgerechnet (nach Rechnungserstellung). */
export function markiereAbgerechnet(
  aufmass: PositionsAufmass | null | undefined,
): PositionsAufmass {
  const basis: PositionsAufmass = aufmass ? { zeilen: [...aufmass.zeilen] } : { zeilen: [] };
  return { zeilen: basis.zeilen.map((z) => ({ ...z, abgerechnet: true })) };
}
